import { DocumentInfo, DocumentsVM } from '@eroman/common/src/views/app/Documents/DocumentsVM';
import { ActionBus } from '@eroman/common/src/_base/actionBus/ActionBus';
import { DatesFormatter } from '@eroman/common/src/views/lib/formatters/DatesFormatter';
import { Nullable } from '@eroman/common/src/_base/lang/Nullable';
import { Document } from '@eroman/common/src/models/documents/Document';
import { DownloadUrlBuilder } from '@eroman/common/src/views/lib/DownloadUrlBuilder';
import { VirtualFileTypes } from '@eroman/common/src/models/documents/VirtualFileTypes';
import { GetPublicDocuments } from '../../../actions/documents/GetPublicDocuments';
import { WebNavigator } from '@eroman/web-client/src/views/lib/navigation/WebNavigator';

export interface DocumentsPublicView {
    modelChanged(model: DocumentsVM);
}

export class DocumentsPublicPresenter {
    private model = new DocumentsVM();
    private breadcrumbs: DocumentInfo[] = [];
    private associationId: Nullable<string> = null;

    constructor(
        private view: DocumentsPublicView,
        private actionBus: ActionBus,
        private navigator: WebNavigator,
        private downloadUrlBuilder: DownloadUrlBuilder,
    ) {}

    async start() {
        const associationId = this.navigator.currentRoute?.params?.associationId;
        this.associationId = associationId;
        await this.loadDocuments(associationId);
    }

    async loadDocuments(associationId: string, id: Nullable<number> = null) {
        this.set({ isLoading: true });
        const documents = await this.actionBus.query(new GetPublicDocuments(associationId, id));
        this.set({ documents: documents.map(document => this.toDocumentInfo(document)), isLoading: false });
    }

    toDocumentInfo(document: Document): DocumentInfo {
        return {
            id: document.id,
            parentId: document.parentId,
            name: document.name,
            physicalPath: document.physicalPath,
            type: document.type,
            url: this.generateUrl(document),
            createdAt: DatesFormatter.fullDateTimeWithoutSeconds(document.createdAt),
            updatedAt: DatesFormatter.fullDateTimeWithoutSeconds(document.updatedAt),
        };
    }

    private generateUrl(document: Document) {
        return document.type === VirtualFileTypes.Regular ?
            this.downloadUrlBuilder.buildFor({ name: document.physicalPath!, label: document.name })
            : null;
    }

    async openFolder(document: DocumentInfo) {
        this.breadcrumbs.push(document);
        this.set({ breadcrumbs: this.breadcrumbs, isLoading: true });
        const documents = await this.actionBus.query(new GetPublicDocuments(this.associationId, document.id));
        this.set({ documents: documents.map(d => this.toDocumentInfo(d)), isLoading: false });
    }

    async goBackInFileSystem() {
        this.set({ isLoading: true });
        let parentId: Nullable<number> = null;
        if (this.breadcrumbs.isNotEmpty()) { parentId = this.breadcrumbs.pop()!!.parentId; }
        const documents = await this.actionBus.query(new GetPublicDocuments(this.associationId, parentId));
        this.set({ breadcrumbs: this.breadcrumbs, documents: documents.map(d => this.toDocumentInfo(d)), isLoading: false });
    }

    set<K extends keyof DocumentsVM>(changes: Pick<DocumentsVM, K>) {
        this.model = Object.assign(this.model, changes);
        this.view.modelChanged(this.model);
    }
}
